import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useMenuItems, type MenuItemDto } from '../../hooks/useMenuItems';

// Map menu names to navigation paths
const menuPathMap: Record<string, string> = {
  Dashboard: '/admin',
  Budget: '/budget-dashboard',
  'Budget Request': '/budgets/request',
  Calendar: '/calendar',
  'Membership Data': '/membership',
  'Engagement Metrics': '/engagement',
  "OKR's and BRG Smart Goals": '/admin/okr-goals',
  'Project Tracking': '/projects',
  'Recognition Requests': '/admin/recognition-requests',
  'Resource Page': '/resources',
};

const pagePathTitleMap: Record<string, string> = {
  '/admin': 'BRG Admin',
  '/admin/brgs/new': 'Create BRG',
  '/admin/members': 'BRG Members',
  '/admin/recognition-requests': 'Recognition Requests',
  '/admin/okr-goals': 'OKR and BRG Smart Goals',
  '/budget-dashboard': 'BRG Budget',
  '/budget': 'Budget',
  '/budgets/request': 'Budget Request',
  '/calendar': 'Calendar',
  '/membership': 'Membership Data',
  '/engagement': 'Engagement Metrics',
  '/goals': 'OKR and BRG Smart Goals',
  '/projects': 'Project Tracking',
  '/resources': 'Resource Page',
};

function getPageTitle(pathname: string): string {
  // Check exact matches first
  if (pagePathTitleMap[pathname]) {
    return pagePathTitleMap[pathname];
  }

  // Check for edit routes
  if (pathname.includes('/admin/brgs/') && pathname.includes('/edit')) {
    return 'Edit BRG';
  }

  // Check for BRG detail page (e.g., /brgs/1, /brgs/123)
  if (/^\/brgs\/\d+$/.test(pathname)) {
    return 'BRG Details';
  }

  return 'BRG Budget';
}

export function NavigationMenu() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { data: menuItems = [], isLoading } = useMenuItems();
  const pageTitle = getPageTitle(location.pathname);

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const closeSidebar = () => {
    setSidebarOpen(false);
  };

  const handleNavigation = (menuName: string) => {
    const path = menuPathMap[menuName] || '/';
    navigate(path);
    closeSidebar();
  };

  const isActive = (menuName: string) => {
    const path = menuPathMap[menuName];
    if (!path) return false;
    return location.pathname === path;
  };

  if (isLoading) {
    return (
      <>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <button
            onClick={toggleSidebar}
            className="hamburger-btn"
            aria-label="Toggle navigation menu"
            title="Open menu"
            disabled
          >
            <span className="hamburger-line"></span>
            <span className="hamburger-line"></span>
            <span className="hamburger-line"></span>
          </button>
          <h1 style={{ margin: 0, fontSize: '24px', fontWeight: 600, color: '#333' }}>
            {pageTitle}
          </h1>
        </div>
      </>
    );
  }

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
        {/* Hamburger Button */}
        <button
          onClick={toggleSidebar}
          className="hamburger-btn"
          aria-label="Toggle navigation menu"
          title="Open menu"
        >
          <span className="hamburger-line"></span>
          <span className="hamburger-line"></span>
          <span className="hamburger-line"></span>
        </button>
        
        {/* Page Title */}
        <h1 style={{ margin: 0, fontSize: '24px', fontWeight: 600, color: '#333' }}>
          {pageTitle}
        </h1>
      </div>

      {/* Overlay */}
      {sidebarOpen && (
        <div
          className="sidebar-overlay"
          onClick={closeSidebar}
          aria-hidden="true"
        />
      )}

      {/* Sidebar */}
      <nav className={`sidebar ${sidebarOpen ? 'open' : ''}`} aria-label="Main navigation">
        <div className="sidebar-header">
          <h2 className="sidebar-title">Menu</h2>
          <button
            className="sidebar-close"
            onClick={closeSidebar}
            aria-label="Close navigation menu"
            title="Close menu"
          >
            ✕
          </button>
        </div>

        <ul className="sidebar-nav">
          {menuItems.map((item: MenuItemDto) => (
            <li key={item.menuId}>
              <button
                onClick={() => handleNavigation(item.menuName)}
                className={`nav-item ${isActive(item.menuName) ? 'active' : ''}`}
                aria-current={isActive(item.menuName) ? 'page' : undefined}
                title={item.menuDesc}
              >
                {item.menuName}
              </button>
            </li>
          ))}
        </ul>
      </nav>
    </>
  );
}
